/**
 * What knitters sent in about a yarn, printed as they sent it.
 *
 * The label above the list is not filler. One of the notes in this catalogue is a
 * prompt injection sample, committed on purpose, and it arrives here looking exactly
 * like the four ordinary notes around it, which is the point of it. This service did
 * not write any of these lines and has no standing to decide which of them a consumer
 * may trust, so it says whose text this is and passes it through whole.
 *
 * A reader who wants to see what happens next should look at the workbench that
 * embeds this page: it quarantines everything that crosses its boundary, shows it in a
 * cordon, and refuses to act on it. That decision belongs there, at the boundary, and
 * not here. A source that sanitised its own output would be asking to be trusted, and
 * an attacker with an account on this site would route around any check made here.
 *
 * So nothing in this component scans, flags, truncates or softens a note, and a later
 * reader should not add any of that. The one thing it does is name the author and the
 * date, which is the honest framing for text somebody else wrote.
 */

import { notesFor, type YarnRecord } from '../catalogue';

export interface NotesProps {
  readonly yarn: YarnRecord;
}

/**
 * The label above the list, which is the same shape whether or not there is a list.
 *
 * A yarn nobody has written in about used to draw a bare italic sentence with no rule
 * above it and no eyebrow, so on a shelf of cards the notes block started in a different
 * place on every card depending on whether anybody had commented. The block is one thing
 * in two states now: the same divider, the same label position, and the count replaced by
 * the reason there is no count.
 */
const label = (count: number): string => {
  if (count === 0) return 'No notes from knitters yet';
  return `${count === 1 ? '1 note' : `${count} notes`} from knitters, as submitted`;
};

export const Notes = ({ yarn }: NotesProps): JSX.Element => {
  const notes = notesFor(yarn.id);

  return (
    <section className="notes" data-notes={notes.length}>
      <p className="eyebrow notes__label">{label(notes.length)}</p>

      {notes.length === 0 ? (
        <p className="notes__none">
          Nobody has written in about this one yet. Anything sent in is printed here
          unedited, as submitted.
        </p>
      ) : (
        <ul className="notes__list">
          {notes.map((note) => (
            <li className="note" key={note.id}>
              <p className="note__body">{note.body}</p>
              <p className="note__by">
                <span aria-hidden="true" className="note__mark">
                  {note.author.slice(0, 1).toUpperCase()}
                </span>
                <span className="note__author">{note.author}</span>
                <span className="note__date num">{note.postedOn}</span>
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
